import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const RANGES = [
  { k: 7, l: "Last 7 days" },
  { k: 30, l: "Last 30 days" },
  { k: 90, l: "Last 90 days" },
];

const AdminAnalytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ series: [], top_products: [] });
  useEffect(() => {
    api.get("/admin/analytics/sales", { params: { days } }).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  const totalRevenue = data.series.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalOrders = data.series.reduce((s, r) => s + (r.orders || 0), 0);

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="font-label text-[11px] uppercase tracking-[0.22em] text-rose/70 mb-1">Overview</p>
          <h1 className="font-hero text-3xl text-cream">Sales analytics</h1>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button
              key={r.k}
              onClick={() => setDays(r.k)}
              className={`px-4 py-2 rounded-full font-label text-[11px] uppercase tracking-[0.22em] transition ${days === r.k ? "text-cream" : "text-cream/80 bg-white/5 hover:text-cream"}`}
              style={days === r.k ? { background: "linear-gradient(135deg, #C9789A, #9B8FBF)" } : {}}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { l: "Revenue", v: formatINR(totalRevenue) },
          { l: "Orders", v: totalOrders },
          { l: "Avg order value", v: totalOrders ? formatINR(Math.round(totalRevenue / totalOrders)) : "—" },
          { l: "Active days", v: data.series.length },
        ].map((k, i) => (
          <div key={i} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="font-label text-[10px] uppercase tracking-[0.22em] text-cream/65 mb-2">{k.l}</p>
            <p className="font-hero text-2xl text-cream">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl p-5 mb-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <p className="font-label text-[11px] uppercase tracking-[0.22em] text-cream/75 mb-4">Revenue trend</p>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={data.series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="g-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9789A" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#C9789A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
              <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
              <Tooltip
                contentStyle={{ background: "#140917", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}
                labelStyle={{ color: "#E8B8CE" }}
                itemStyle={{ color: "#FBF7F4" }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#C9789A" strokeWidth={2} fill="url(#g-rev)" name="Revenue" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <p className="font-label text-[11px] uppercase tracking-[0.22em] text-cream/75 mb-4">Top products</p>
        {data.top_products.length === 0 ? (
          <p className="text-cream/65">Not enough data yet.</p>
        ) : (
          <div className="space-y-2">
            {data.top_products.map((p, i) => (
              <div key={p.product_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition">
                <span className="font-hero text-xl text-cream/60 w-6 text-center">{i + 1}</span>
                <div className="w-11 h-12 rounded-md bg-white/5 overflow-hidden flex-shrink-0">
                  {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-cormorant text-base text-cream truncate">{p.name}</p>
                  <p className="text-xs text-cream/65">Sold {p.qty}</p>
                </div>
                <p className="font-body text-sm text-cream">{formatINR(p.revenue)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
